import { GraphNode, OwnerGraph, graphKey } from '../../../services/owner-graph.service';

/**
 * La mise en page du graphe d'un propriétaire : une fonction PURE, graphe en
 * entrée, cartes positionnées et tracés en sortie. Aucune mesure du DOM — la
 * hauteur de carte est fixe —, ce qui rend le dessin stable d'une ouverture à
 * l'autre et testable sans navigateur.
 *
 * Cinq colonnes : propriétaire · biens · baux · locataires · autres
 * propriétaires (seulement quand un locataire loue aussi ailleurs). Chaque bien
 * ouvre un bloc : ses baux en face de lui, les locataires de ces baux en face
 * d'eux. Un locataire de deux baux n'a qu'une carte, rattachée à la première ;
 * la seconde arête remonte vers elle — c'est pour cela qu'un arbre ECharts ne
 * convenait pas.
 */

export const CARD_W = 208;
export const CARD_H = 56;
export const COL_GAP = 56;
const ROW_GAP = 10;
const BLOCK_GAP = 18;
export const HEADER_H = 28;

const ROW_H = CARD_H + ROW_GAP;

export type GraphView = 'local' | 'full';

export const COLUMN_TITLES = ['Propriétaire', 'Biens', 'Baux', 'Locataires', 'Autres propriétaires'] as const;

export interface LayoutCard {
  key: string;
  col: number;
  x: number;
  y: number;
  /** Le nœud ; absent sur la carte « n baux terminés » d'un bien replié. */
  node: GraphNode | null;
  /** Carte de repli : le bien auquel elle appartient et le nombre de baux qu'elle cache. */
  fold?: { propertyKey: string; count: number };
  anchor: boolean;
}

export interface LayoutEdge {
  from: string;
  to: string;
  path: string;
}

export interface GraphLayout {
  cards: LayoutCard[];
  edges: LayoutEdge[];
  columns: { title: string; x: number }[];
  width: number;
  height: number;
}

export const foldKey = (propertyKey: string): string => `fold:${propertyKey}`;

/**
 * La vue locale : la fiche ancrée et ses voisins immédiats, en propriétaires,
 * biens et baux — les locataires et propriétaires externes s'en déduisent au
 * moment de la mise en page, à partir des baux visibles.
 *
 *   bail        → son bien, son propriétaire (et ses locataires)
 *   bien        → son propriétaire, ses baux (et leurs locataires)
 *   locataire   → ses baux, leurs biens, leurs propriétaires
 *   propriétaire→ tout son graphe : la vue locale EST la vue complète
 */
export function localKeys(graph: OwnerGraph): Set<string> {
  const { anchor, nodes } = graph;
  const keys = new Set<string>();
  const get = (kind: GraphNode['kind'], id: string | null) => (id ? nodes.get(graphKey(kind, id)) : undefined);
  const addPropertyChain = (propertyId: string | null) => {
    const property = get('property', propertyId);
    if (!property) return;
    keys.add(property.key);
    const owner = get('owner', property.parentId);
    if (owner) keys.add(owner.key);
  };

  switch (anchor.kind) {
    case 'owner':
      for (const n of nodes.values()) {
        if (n.kind !== 'tenant' && !n.external) keys.add(n.key);
      }
      break;
    case 'property':
      addPropertyChain(anchor.id);
      for (const n of nodes.values()) {
        if (n.kind === 'lease' && n.parentId === anchor.id) keys.add(n.key);
      }
      break;
    case 'lease': {
      const lease = get('lease', anchor.id);
      if (lease) {
        keys.add(lease.key);
        addPropertyChain(lease.parentId);
      }
      break;
    }
    case 'tenant':
      for (const n of nodes.values()) {
        if (n.kind === 'lease' && n.tenantIds.includes(anchor.id)) {
          keys.add(n.key);
          addPropertyChain(n.parentId);
        }
      }
      break;
  }
  return keys;
}

/** La vue n'a rien à ajouter quand elle part d'un propriétaire. */
export function hasDistinctFullView(graph: OwnerGraph): boolean {
  return graph.anchor.kind !== 'owner';
}

const byLabel = (a: GraphNode, b: GraphNode) => a.label.localeCompare(b.label, 'fr');

/** Baux en cours d'abord, puis les plus récents. */
const leaseOrder = (a: GraphNode, b: GraphNode) =>
  Number(a.ended) - Number(b.ended) || (b.startDate ?? '').localeCompare(a.startDate ?? '');

const colX = (col: number) => col * (CARD_W + COL_GAP);

/** Courbe du bord droit de `a` au bord gauche de `b`. */
function edgePath(a: LayoutCard, b: LayoutCard): string {
  const x1 = a.x + CARD_W;
  const y1 = a.y + CARD_H / 2;
  const x2 = b.x;
  const y2 = b.y + CARD_H / 2;
  const mx = (x1 + x2) / 2;
  return `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
}

export function layoutGraph(
  graph: OwnerGraph,
  view: GraphView,
  expanded: ReadonlySet<string>,
): GraphLayout {
  const { anchor, nodes } = graph;
  const anchorKey = graphKey(anchor.kind, anchor.id);
  const all = [...nodes.values()];

  const base = view === 'full'
    ? new Set(all.filter(n => n.kind !== 'tenant' && !n.external).map(n => n.key))
    : localKeys(graph);

  // Les baux qu'on ne replie jamais : la fiche ouverte, et les baux du
  // locataire ouvert — c'est d'eux qu'on est venu parler.
  const pinned = (n: GraphNode) =>
    n.key === anchorKey || (anchor.kind === 'tenant' && n.tenantIds.includes(anchor.id));

  const owners = all.filter(n => n.kind === 'owner' && !n.external && base.has(n.key)).sort(byLabel);
  const properties = all.filter(n => n.kind === 'property' && base.has(n.key)).sort(byLabel);
  const propertyIds = new Set(properties.map(p => p.id));

  const leasesByProperty = new Map<string, GraphNode[]>();
  const hiddenByProperty = new Map<string, number>();
  const orphanLeases: GraphNode[] = [];
  for (const lease of all.filter(n => n.kind === 'lease' && base.has(n.key)).sort(leaseOrder)) {
    const parent = lease.parentId && propertyIds.has(lease.parentId) ? lease.parentId : null;
    if (!parent) {
      orphanLeases.push(lease);
      continue;
    }
    const propertyKey = graphKey('property', parent);
    if (lease.ended && !pinned(lease) && !expanded.has(propertyKey)) {
      hiddenByProperty.set(parent, (hiddenByProperty.get(parent) ?? 0) + 1);
      continue;
    }
    leasesByProperty.set(parent, [...(leasesByProperty.get(parent) ?? []), lease]);
  }

  const cards = new Map<string, LayoutCard>();
  const edges: LayoutEdge[] = [];
  const links: [string, string][] = [];
  let cursor = HEADER_H;
  let externalCursor = HEADER_H;

  const place = (key: string, node: GraphNode | null, col: number, y: number, fold?: LayoutCard['fold']) => {
    const card: LayoutCard = { key, node, col, x: colX(col), y, fold, anchor: key === anchorKey };
    cards.set(key, card);
    return card;
  };

  /** Un bloc : des baux (et leurs locataires) en face d'un bien, ou de rien. */
  const placeBlock = (property: GraphNode | null, leases: GraphNode[], hidden: number) => {
    const top = cursor;
    let leaseRow = 0;
    let tenantRow = 0;
    if (property) place(property.key, property, 1, top);

    for (const lease of leases) {
      place(lease.key, lease, 2, top + leaseRow * ROW_H);
      leaseRow++;
      if (property) links.push([property.key, lease.key]);
      for (const tenantId of lease.tenantIds) {
        const tenant = nodes.get(graphKey('tenant', tenantId));
        if (!tenant) continue;
        if (!cards.has(tenant.key)) {
          place(tenant.key, tenant, 3, top + tenantRow * ROW_H);
          tenantRow++;
          placeExternals(tenant);
        }
        links.push([lease.key, tenant.key]);
      }
    }
    if (property && hidden > 0) {
      const key = foldKey(property.key);
      place(key, null, 2, top + leaseRow * ROW_H, { propertyKey: property.key, count: hidden });
      leaseRow++;
      links.push([property.key, key]);
    }
    const rows = Math.max(1, leaseRow, tenantRow);
    cursor = top + rows * ROW_H - ROW_GAP + BLOCK_GAP;
    return top;
  };

  const placeExternals = (tenant: GraphNode) => {
    for (const ownerId of tenant.otherOwnerIds) {
      const owner = nodes.get(graphKey('owner', ownerId));
      if (!owner) continue;
      if (!cards.has(owner.key)) {
        const tenantY = cards.get(tenant.key)!.y;
        const y = Math.max(externalCursor, tenantY);
        place(owner.key, owner, 4, y);
        externalCursor = y + ROW_H;
      }
      links.push([tenant.key, owner.key]);
    }
  };

  for (const owner of owners) {
    const own = properties.filter(p => p.parentId === owner.id);
    const top = cursor;
    if (own.length === 0) {
      cursor += ROW_H - ROW_GAP + BLOCK_GAP;
    }
    for (const property of own) {
      placeBlock(property, leasesByProperty.get(property.id) ?? [], hiddenByProperty.get(property.id) ?? 0);
      links.push([owner.key, property.key]);
    }
    place(owner.key, owner, 0, top);
  }

  // Les biens sans propriétaire dans la vue, puis les baux sans bien.
  const ownerIds = new Set(owners.map(o => o.id));
  for (const property of properties.filter(p => !p.parentId || !ownerIds.has(p.parentId))) {
    placeBlock(property, leasesByProperty.get(property.id) ?? [], hiddenByProperty.get(property.id) ?? 0);
  }
  if (orphanLeases.length) placeBlock(null, orphanLeases, 0);

  // Le locataire ouvert qui n'a aucun bail visible : seul dans sa colonne.
  if (anchor.kind === 'tenant' && !cards.has(anchorKey)) {
    const tenant = nodes.get(anchorKey);
    if (tenant) {
      place(tenant.key, tenant, 3, cursor);
      placeExternals(tenant);
      cursor += ROW_H - ROW_GAP + BLOCK_GAP;
    }
  }

  const seen = new Set<string>();
  for (const [from, to] of links) {
    const a = cards.get(from);
    const b = cards.get(to);
    const id = `${from}>${to}`;
    if (!a || !b || seen.has(id)) continue;
    seen.add(id);
    edges.push({ from, to, path: edgePath(a, b) });
  }

  const list = [...cards.values()];
  const maxCol = list.some(c => c.col === 4) ? 4 : 3;
  return {
    cards: list,
    edges,
    columns: COLUMN_TITLES.slice(0, maxCol + 1).map((title, col) => ({ title, x: colX(col) })),
    width: colX(maxCol) + CARD_W,
    height: Math.max(cursor - BLOCK_GAP, externalCursor - ROW_GAP, HEADER_H + CARD_H),
  };
}

export type Direction = 'up' | 'down' | 'left' | 'right';

/**
 * La carte vers laquelle une flèche déplace le curseur. ↑/↓ restent dans la
 * colonne ; ←/→ suivent d'abord les arêtes, et à défaut prennent la carte la
 * plus proche en hauteur dans la colonne voisine.
 */
export function neighbor(layout: GraphLayout, fromKey: string, direction: Direction): string | null {
  const from = layout.cards.find(c => c.key === fromKey);
  if (!from) return layout.cards[0]?.key ?? null;

  if (direction === 'up' || direction === 'down') {
    const column = layout.cards.filter(c => c.col === from.col).sort((a, b) => a.y - b.y);
    const i = column.indexOf(from) + (direction === 'down' ? 1 : -1);
    return column[i]?.key ?? null;
  }

  const col = from.col + (direction === 'right' ? 1 : -1);
  const linked = layout.edges
    .filter(e => (direction === 'right' ? e.from === fromKey : e.to === fromKey))
    .map(e => (direction === 'right' ? e.to : e.from));
  const candidates = layout.cards.filter(c =>
    c.col === col && (linked.length === 0 || linked.includes(c.key)));
  if (!candidates.length) return null;
  return candidates.reduce((best, c) =>
    Math.abs(c.y - from.y) < Math.abs(best.y - from.y) ? c : best).key;
}
